import React, { useCallback, useEffect, useState } from 'react'
import { keyBy, groupBy, flatten, chunk } from 'lodash'
import SubscriptionService from '../services/SubscriptionService'
import LicenseTable from './LicenseTable'
import FadedLineHeader from '../../common/components/fadedLineHeader/FadedLineHeader'
import { APP_ID_LOGGING_SERVICE, APP_ID_PRISMA_ACCESS_EDITION, APP_ID_SDWAN } from './common/GlobalConstants'

import type { VFC } from 'react'
import type { LicenseTableData } from './LicenseTable'
import { Overline } from '@panwds/react-ui'

const GROUP_LICENSE_APPS = new Set(['strata_insights', 'zingbox_ngfw', 'sspm', 'ng_casb', 'zingbox'])
const ALLOW_APPS = new Set([APP_ID_SDWAN, APP_ID_PRISMA_ACCESS_EDITION, APP_ID_LOGGING_SERVICE])

const LicensePage: VFC<any> = (props) => {
  const { apps = {}, auth, globalTsgId } = props
  const [licenseData, setLicenseData] = useState<LicenseTableData[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const transferLicenses = useCallback((licenseData, cspAccounts): LicenseTableData[] => {
    const filterLicenseData = licenseData.filter((each: any) => ALLOW_APPS.has(each.app_id))
    const cspAccountIdToNameMap = keyBy(cspAccounts, 'accountid')

    const _licenses: LicenseTableData[] = []
    const groupedLicenses = groupBy(filterLicenseData, (each) => {
      return `${each.app_id}|${each.activation_info?.support_account_id}`
    })
    for (const [key, groupedAppLicenses] of Object.entries(groupedLicenses)) {
      const [app_id, support_account_id] = key.split('|')
      if (GROUP_LICENSE_APPS.has(app_id)) {
        const allocated_size = groupedAppLicenses.filter(each => each.is_activated).length
        const remained_size = groupedAppLicenses.filter(each => !each.is_activated).length
        _licenses.push({
          license_id: groupedAppLicenses[0].license_id,
          app_id,
          support_account_name: cspAccountIdToNameMap[support_account_id].name,
          support_account_id,
          entitlements: [{
            app_id,
            purchased_size: groupedAppLicenses.length,
            allocated_size,
            remained_size,
            purchased_unit: groupedAppLicenses[0].entitlements[0].purchased_unit,
          }],
        })
      }
      else {
        groupedAppLicenses.forEach(each => {
          _licenses.push({
            license_id: each.license_id,
            app_id,
            support_account_name: cspAccountIdToNameMap[support_account_id].name,
            support_account_id,
            entitlements: each.entitlements,
            is_license_remaining: each.calculated_filters?.is_license_remaining,
            associated_tenants: each.calculated_filters?.associated_tenants,
            csp_serial_number: each.activation_info?.csp_serial_number,
            label: each.label,
          })
        })
      }
    }
    return _licenses
  }, [])

  useEffect(() => {
    const CHUNK_SIZE = 3
    const app_ids = Array.from(ALLOW_APPS).join(',')
    async function fetchData() {
      const cspAccounts = auth.user.supportAccountIds
      // TODO: right now make each call in chunks based on support_account_id, need to make pagination call here
      let data: any
      if (cspAccounts.length > 5) {
        const cspIds: string[] = cspAccounts.map((each: { accountid: string }) => each.accountid)
        const cspChunks = chunk(cspIds, CHUNK_SIZE)
        const licensePromises = cspChunks.map((support_account_ids) => {
          return SubscriptionService.licenses.get({
            support_account_ids,
            tsg_id: globalTsgId,
            app_id: app_ids,
          })
        })
        const resp = await Promise.all(licensePromises)
        data = flatten(resp.map(each => each.data))
      }
      else {
        const resp = await SubscriptionService.licenses.get({
          support_account_ids: cspAccounts.map((each: { accountid: any }) => each.accountid),
          tsg_id: globalTsgId,
          app_id: app_ids,
        })
        data = resp.data
      }
      setLicenseData(transferLicenses(data, cspAccounts))
      setLoading(false)
    }
    fetchData()
  }, [auth.user.email, auth.user.supportAccountIds, globalTsgId, transferLicenses])
  return (
    <div className='cm-w-full cm-max-w-[1500px]'>
      <hr className='cm-m-8'/>
      <LicenseTable tableData={licenseData} apps={apps} loading={loading} />
    </div>
  )
}

export default LicensePage
